/*
 * Enhanced Product Navigation for Shopify Rise Theme
 * Version: 2.0.0
 * 
 * Key Improvements:
 * - Robust sort order detection with multiple fallbacks
 * - Cross-tab synchronization
 * - Comprehensive error handling
 * - Detailed debugging
 * - Performance optimizations
 */


(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    // DOM Selectors
    selectors: {
      // Product links in collection
      collectionProduct: '.grid-view-item__link, a.grid-view-item__link, .card-wrapper .card__heading a, [data-product-handle] a',
      // Navigation buttons
      prevButton: '[id^="ai-prev-button-"], [id*="prev"][id*="button"], .product-navigation__button--prev',
      nextButton: '[id^="ai-next-button-"], [id*="next"][id*="button"], .product-navigation__button--next',
      // Sort controls
      sortSelect: 'select[name="sort_by"], #SortBy, [data-sort-by]',
      sortButton: '[data-sort], [data-sort-by]',
      activeSort: '.collection-sorting .active, [data-sort-by] .active, .active[data-sort], .active[data-value]',
      // Price elements for price-based sorting
      priceElement: '.price-item, [data-price], .product-price, .price',
      // Collection elements
      collectionGrid: '.grid--view-items, .product-grid, .collection, [data-section-type="collection-template"]'
    },
    // Storage keys
    storageKeys: {
      productOrder: (collection) => `productNav_${collection}_order_v2`,
      sortOrder: (collection) => `productNav_${collection}_sort_v2`,
      collectionData: 'productNav_collections_v2'
    },
    // Behavior settings
    maxRetries: 5,
    retryDelay: 500,
    debounceDelay: 300,
    dataVersion: '2.0.0',
    // Debug settings
    debug: window.location.search.includes('debug=navigation')
  };

  // State management
  const state = {
    currentHandle: null,
    productHandles: [],
    sortBy: 'manual',
    collection: null,
    isInitialized: false,
    isProcessing: false,
    observers: [],
    cleanupHandlers: [],
    lastSortCheck: 0,
    currentIndex: -1
  };

  // Debug utilities
  const debug = {
    log: (message, data = null, level = 'log') => {
      if (!CONFIG.debug) return;
      
      const timestamp = new Date().toISOString().substr(11, 12);
      const logMessage = `[${timestamp}] [ProductNav] ${message}`;
      
      // Log to console
      if (data !== null) {
        console[level](logMessage, data);
      } else {
        console[level](logMessage);
      }
      
      // Add to debug panel if available
      debug.addToPanel(logMessage, data, level);
    },
    
    initPanel: () => {
      if (document.getElementById('product-nav-debug')) return;
      
      const panel = document.createElement('div');
      panel.id = 'product-nav-debug';
      panel.style.cssText = `
        position: fixed;
        bottom: 0;
        right: 0;
        width: 400px;
        max-height: 300px;
        overflow-y: auto;
        background: rgba(0,0,0,0.9);
        color: #fff;
        font-family: monospace;
        font-size: 12px;
        padding: 10px;
        z-index: 9999;
        border-top: 2px solid #ff00ff;
        display: ${CONFIG.debug ? 'block' : 'none'};
      `;
      
      const toggleBtn = document.createElement('button');
      toggleBtn.textContent = 'Debug';
      toggleBtn.style.cssText = `
        position: absolute;
        top: -25px;
        right: 0;
        background: #ff00ff;
        border: none;
        color: white;
        padding: 2px 10px;
        cursor: pointer;
      `;
      toggleBtn.onclick = () => {
        panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
      };
      
      panel.appendChild(toggleBtn);
      document.body.appendChild(panel);
    },
    
    addToPanel: (message, data, level) => {
      if (!CONFIG.debug) return;
      
      const panel = document.getElementById('product-nav-debug') || debug.initPanel();
      if (!panel) return;
      
      const entry = document.createElement('div');
      entry.style.cssText = `
        padding: 5px;
        border-bottom: 1px solid #333;
        color: ${level === 'error' ? '#ff6b6b' : level === 'warn' ? '#ffcc00' : '#fff'};
      `;
      
      entry.textContent = message;
      
      if (data) {
        const dataEl = document.createElement('pre');
        dataEl.style.cssText = 'margin: 5px 0 0 10px; font-size: 11px; color: #aaa;';
        dataEl.textContent = JSON.stringify(data, null, 2);
        entry.appendChild(dataEl);
      }
      
      panel.insertBefore(entry, panel.firstChild);
      
      // Limit number of entries
      const entries = panel.querySelectorAll('div');
      if (entries.length > 50) {
        panel.removeChild(entries[entries.length - 1]);
      }
    }
  };

  // Initialize debug panel
  if (CONFIG.debug) {
    debug.initPanel();
    debug.log('Debug mode enabled');
  }

  // Utility functions
  const utils = {
    // Check if current page is password protected
    isPasswordPage() {
      return (
        document.body.classList.contains('password-page') ||
        !!document.querySelector('form[action*="/password"], form[action*="password"]') ||
        window.location.pathname.includes('password')
      );
    },

    // Get current product handle from URL
    getCurrentHandle() {
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      return pathParts[pathParts.length - 1] || null;
    },

    // Get current sort order with multiple fallbacks
    getCurrentSortOrder() {
      // 1. Check URL parameters first
      const urlParams = new URLSearchParams(window.location.search);
      let sortBy = urlParams.get('sort_by');
      
      // 2. Check sort dropdown
      if (!sortBy) {
        const sortSelect = document.querySelector(CONFIG.selectors.sortSelect);
        sortBy = sortSelect?.value || sortSelect?.dataset?.currentSort;
      }
      
      // 3. Check active sort buttons
      if (!sortBy) {
        const activeSort = document.querySelector(CONFIG.selectors.activeSort);
        if (activeSort) {
          sortBy = 
            activeSort.closest('[data-sort-by]')?.dataset.sortBy || 
            activeSort.closest('[data-sort]')?.dataset.sort ||
            activeSort.closest('[data-value]')?.dataset.value ||
            activeSort.dataset.value;
        }
      }
      
      // 4. Check for sort indicators in the DOM
      if (!sortBy) {
        const sortIndicator = document.querySelector('[data-sort-active]');
        if (sortIndicator) {
          sortBy = sortIndicator.dataset.sortActive;
        }
      }
      
      debug.log('Detected sort order', { sortBy, fallback: sortBy || 'manual (fallback)' });
      return sortBy || 'manual';
    },
    
    // Safe JSON parse with error handling
    safeParse(json, defaultValue = null) {
      try {
        return json ? JSON.parse(json) : defaultValue;
      } catch (e) {
        debug.log('Error parsing JSON', { error: e.message, json }, 'error');
        return defaultValue;
      }
    },
    
    // Debounce function
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },
    
    // Get collection handle from URL
    getCollectionHandle() {
      const pathParts = window.location.pathname.split('/');
      const collectionsIndex = pathParts.indexOf('collections');
      return collectionsIndex > -1 ? pathParts[collectionsIndex + 1] : null;
    },
    
    // Generate a unique ID for the current collection state
    getCollectionStateId(collection, sortBy) {
      return `${collection}_${sortBy}`;
    },
    
    // Get all collection products with their data
    getCollectionProducts() {
      const products = Array.from(document.querySelectorAll(CONFIG.selectors.collectionProduct))
        .map(link => {
          try {
            const url = new URL(link.href, window.location.origin);
            const pathParts = url.pathname.split('/').filter(Boolean);
            const handle = pathParts[pathParts.length - 1];
            
            if (!handle) return null;
            
            // Try to get additional product data
            const card = link.closest('.grid-view-item, .card, [data-product-handle]') || {};
            const priceEl = card.querySelector(CONFIG.selectors.priceElement);
            const price = priceEl ? priceEl.textContent.trim().replace(/[^0-9.]/g, '') : '0';
            const title = card.querySelector('.card__title, .product-card__title, [data-product-title]')?.textContent.trim() || '';
            
            return {
              handle,
              url: url.pathname,
              title,
              price: parseFloat(price) || 0,
              element: link
            };
          } catch (e) {
            debug.log('Error processing product link', { error: e, href: link.href }, 'error');
            return null;
          }
        })
        .filter(Boolean);
      
      debug.log('Collected products from page', { count: products.length });
      return products;
    }
  };

  // State management
  const stateManager = {
    // Save product order to storage
    saveProductOrder(products, sortBy, collection) {
      if (!collection || !products.length) {
        debug.log('Cannot save product order: missing collection or products', { collection, productCount: products.length }, 'warn');
        return false;
      }
      
      const stateId = utils.getCollectionStateId(collection, sortBy);
      const data = {
        version: CONFIG.dataVersion,
        handles: products.map(p => p.handle),
        sortBy,
        collection,
        timestamp: Date.now(),
        stateId
      };
      
      try {
        // Save to localStorage for persistence
        localStorage.setItem(
          CONFIG.storageKeys.productOrder(collection),
          JSON.stringify(data)
        );
        
        // Also save to sessionStorage for current session
        sessionStorage.setItem(
          CONFIG.storageKeys.productOrder(collection),
          JSON.stringify(data)
        );
        
        debug.log('Saved product order', { 
          collection, 
          sortBy, 
          productCount: products.length,
          stateId 
        });
        
        return true;
      } catch (e) {
        debug.log('Error saving product order', { error: e.message }, 'error');
        return false;
      }
    },
    
    // Load product order from storage
    loadProductOrder(collection, sortBy) {
      if (!collection) {
        debug.log('Cannot load product order: no collection specified', null, 'warn');
        return null;
      }
      
      // Try sessionStorage first (fresher data)
      let data = utils.safeParse(
        sessionStorage.getItem(CONFIG.storageKeys.productOrder(collection))
      );
      
      // Fall back to localStorage
      if (!data) {
        data = utils.safeParse(
          localStorage.getItem(CONFIG.storageKeys.productOrder(collection))
        );
      }
      
      // Validate the loaded data
      if (!data || !data.handles || !Array.isArray(data.handles)) {
        debug.log('No valid product order found in storage', { collection }, 'warn');
        return null;
      }
      
      // Check if the sort order matches
      if (sortBy && data.sortBy !== sortBy) {
        debug.log('Sort order mismatch, ignoring stored order', { 
          storedSort: data.sortBy, 
          currentSort: sortBy 
        }, 'warn');
        return null;
      }
      
      debug.log('Loaded product order from storage', { 
        collection, 
        sortBy: data.sortBy, 
        productCount: data.handles.length,
        age: data.timestamp ? Math.floor((Date.now() - data.timestamp) / 1000) + 's old' : 'unknown'
      });
      
      return data;
    },
    
    // Clean up old/stale data
    cleanupOldData() {
      const now = Date.now();
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 1 week
      
      // Clean localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith('productNav_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data.timestamp && now - data.timestamp > maxAge) {
              localStorage.removeItem(key);
              debug.log('Removed stale data', { key, age: now - data.timestamp });
            }
          } catch (e) {
            // Invalid JSON, remove it
            localStorage.removeItem(key);
          }
        }
      }
    }
  };

  // Navigation core functionality
  const navigation = {
    // Initialize the navigation system
    init() {
      try {
        debug.log('=== Starting navigation initialization ===');
        
        if (state.isInitialized) {
          debug.log('Navigation already initialized, cleaning up first...');
          this.cleanup();
        }
        
        if (state.isProcessing) {
          debug.log('Navigation is already processing, aborting new initialization', null, 'warn');
          return;
        }
        
        state.isProcessing = true;
        
        // Reset critical state
        state.currentHandle = utils.getCurrentHandle();
        state.sortBy = utils.getCurrentSortOrder();
        state.collection = utils.getCollectionHandle();
        
        debug.log('Initializing navigation with state:', {
          handle: state.currentHandle,
          sortBy: state.sortBy,
          collection: state.collection,
          isCollectionPage: this.isCollectionPage(),
          isProductPage: this.isProductPage()
        });
        
        // Add a small delay to ensure DOM is ready
        const initNavigation = () => {
          try {
            // Initialize debug panel first
            if (CONFIG.debug) {
              debug.initPanel();
            }
            
            // Initialize styles
            this.initStyles();
            
            // Set up event listeners
            this.setupEventListeners();
            
            // Initial setup based on page type
            if (this.isCollectionPage()) {
              this.setupCollectionPage();
            } else if (this.isProductPage()) {
              this.setupProductPage();
            } else {
              debug.log('Not a collection or product page, skipping initialization');
            }
            
            // Clean up old data
            stateManager.cleanupOldData();
            
            state.isInitialized = true;
            state.isProcessing = false;
            debug.log('=== Navigation initialization completed successfully ===');
            
          } catch (error) {
            debug.log('Error during navigation initialization', { 
              error: error.message,
              stack: error.stack 
            }, 'error');
            state.isProcessing = false;
            this.cleanup();
          }
        };
        
        // Small delay to ensure DOM is ready
        setTimeout(initNavigation, 100);
        
      } catch (error) {
        debug.log('Fatal error in navigation initialization', {
          error: error.message,
          stack: error.stack
        }, 'error');
        state.isProcessing = false;
        state.isInitialized = false;
      }
    },
    
    // Set up collection page
    setupCollectionPage() {
      debug.log('Setting up collection page');
      
      // Initial save of product order
      this.saveProductOrder();
      
      // Watch for changes to the product grid
      this.watchForProductGridChanges();
      
      // Set up sort change detection
      this.setupSortChangeDetection();
    },
    
    // Set up product page
    setupProductPage() {
      debug.log('Setting up product page');
      
      // Try to load saved product order
      const savedData = stateManager.loadProductOrder(state.collection, state.sortBy);
      
      if (savedData) {
        state.productHandles = savedData.handles;
        state.sortBy = savedData.sortBy || state.sortBy;
        
        // Set up navigation buttons
        this.setupProductNavigation();
      } else {
        debug.log('No saved product order found, will retry...');
        // Try to find collection and load its products
        this.findAndLoadCollection();
      }
    },
    
    // Find and load collection data
    findAndLoadCollection() {
      debug.log('Attempting to find and load collection data');
      
      // Look for collection links in breadcrumbs or related elements
      const collectionLinks = document.querySelectorAll('a[href*="/collections/"]');
      let collectionUrl = null;
      
      for (const link of collectionLinks) {
        const href = link.getAttribute('href');
        if (href && href.includes('/collections/') && !href.includes(state.currentHandle)) {
          collectionUrl = new URL(href, window.location.origin).pathname;
          break;
        }
      }
      
      if (collectionUrl) {
        debug.log('Found collection URL, attempting to load', { collectionUrl });
        this.loadCollectionData(collectionUrl);
      } else {
        debug.log('Could not find collection URL, will retry...');
        // Set up a retry mechanism
        this.retryCollectionLoad();
      }
    },
    
    // Load collection data via fetch
    async loadCollectionData(collectionUrl) {
      try {
        debug.log('Loading collection data', { url: collectionUrl });
        
        const response = await fetch(collectionUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract product handles from the collection page
        const productLinks = Array.from(doc.querySelectorAll(CONFIG.selectors.collectionProduct));
        const handles = productLinks
          .map(link => {
            const url = new URL(link.href, window.location.origin);
            const pathParts = url.pathname.split('/').filter(Boolean);
            return pathParts[pathParts.length - 1];
          })
          .filter(handle => handle && handle !== state.currentHandle);
        
        if (handles.length > 0) {
          state.productHandles = handles;
          this.setupProductNavigation();
          
          // Save the loaded order
          stateManager.saveProductOrder(
            handles.map(handle => ({ handle })),
            state.sortBy,
            state.collection
          );
        } else {
          debug.log('No product handles found in collection', { url: collectionUrl }, 'warn');
          this.retryCollectionLoad();
        }
      } catch (error) {
        debug.log('Error loading collection data', { error: error.message }, 'error');
        this.retryCollectionLoad();
      }
    },
    
    // Retry loading collection data
    retryCollectionLoad(attempt = 0) {
      if (attempt >= 3) {
        debug.log('Max retries reached, giving up on loading collection data', null, 'warn');
        return;
      }
      
      const delay = (attempt + 1) * 2000; // Exponential backoff
      debug.log(`Retrying collection load in ${delay}ms`, { attempt });
      
      setTimeout(() => {
        this.findAndLoadCollection();
      }, delay);
    },
    
    // Set up event listeners
    setupEventListeners() {
      debug.log('Setting up event listeners');
      
      // Clean up any existing event listeners first
      if (state.cleanupHandlers && state.cleanupHandlers.length > 0) {
        debug.log('Cleaning up existing event listeners');
        state.cleanupHandlers.forEach(handler => {
          try { handler(); } catch (e) {}
        });
        state.cleanupHandlers = [];
      }
      
      // Handle sort dropdown changes
      const sortSelect = document.querySelector(CONFIG.selectors.sortSelect);
      if (sortSelect) {
        sortSelect.addEventListener('change', () => this.handleSortChange());
      }
      
      // Handle AJAX sort buttons
      document.body.addEventListener('click', (e) => {
        if (e.target.closest(CONFIG.selectors.sortButton)) {
          debug.log('Sort button clicked, waiting for sort to complete...');
          setTimeout(() => this.handleSortChange(), CONFIG.retryDelay);
        }
      });
      
      // Handle URL changes (for AJAX navigation)
      let lastUrl = location.href;
      const urlObserver = new MutationObserver(() => {
        if (location.href !== lastUrl) {
          lastUrl = location.href;
          if (location.search.includes('sort_by=')) {
            debug.log('URL changed, checking for sort changes...');
            this.handleSortChange();
          }
        }
      });
      
      urlObserver.observe(document, { 
        subtree: true, 
        childList: true,
        attributes: true
      });
      
      // Store observer for cleanup
      state.observers.push(urlObserver);
      
      // Handle storage events (for cross-tab synchronization)
      const storageHandler = (e) => {
        if (e.key && e.key.startsWith('productNav_') && e.newValue) {
          try {
            const data = JSON.parse(e.newValue);
            if (data.collection === state.collection && data.sortBy === state.sortBy) {
              debug.log('Storage event received, updating product order', {
                source: e.key,
                productCount: data.handles?.length
              });
              
              state.productHandles = data.handles || [];
              if (this.isProductPage()) {
                this.setupProductNavigation();
              }
            }
          } catch (e) {
            debug.log('Error handling storage event', { error: e.message }, 'error');
          }
        }
      };
      
      window.addEventListener('storage', storageHandler);
      
      // Store handler for cleanup
      state.cleanupHandlers = state.cleanupHandlers || [];
      state.cleanupHandlers.push(() => {
        window.removeEventListener('storage', storageHandler);
      });
      
      // Handle page visibility changes
      const visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          debug.log('Page became visible, checking for updates...');
          this.handleSortChange();
        }
      };
      
      document.addEventListener('visibilitychange', visibilityHandler);
      state.cleanupHandlers.push(() => {
        document.removeEventListener('visibilitychange', visibilityHandler);
      });
      
      // Handle beforeunload for cleanup
      window.addEventListener('beforeunload', this.cleanup.bind(this));
    },
    
    // Watch for changes to the product grid
    watchForProductGridChanges() {
      const grid = document.querySelector(CONFIG.selectors.collectionGrid);
      if (!grid) {
        debug.log('No product grid found, will retry...');
        setTimeout(() => this.watchForProductGridChanges(), 1000);
        return;
      }
      
      const observer = new MutationObserver(utils.debounce((mutations) => {
        const hasRelevantChanges = mutations.some(mutation => {
          // Check if the mutation affected product elements
          return Array.from(mutation.addedNodes).some(node => 
            node.nodeType === 1 && (
              node.matches(CONFIG.selectors.collectionProduct) || 
              node.querySelector(CONFIG.selectors.collectionProduct)
            )
          ) || 
          mutation.type === 'attributes' && (
            mutation.target.matches(CONFIG.selectors.collectionProduct) ||
            mutation.target.closest(CONFIG.selectors.collectionProduct)
          );
        });
        
        if (hasRelevantChanges) {
          debug.log('Product grid changed, updating product order...');
          this.saveProductOrder();
        }
      }, 500));
      
      observer.observe(grid, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'data-sort']
      });
      
      state.observers.push(observer);
    },
    
    // Set up sort change detection
    setupSortChangeDetection() {
      // Check for sort changes periodically
      let lastSortBy = state.sortBy;
      
      const checkSortChange = () => {
        if (state.isProcessing) return;
        
        const currentSort = utils.getCurrentSortOrder();
        if (currentSort !== lastSortBy) {
          debug.log('Sort order changed detected', { 
            from: lastSortBy, 
            to: currentSort 
          });
          
          lastSortBy = currentSort;
          state.sortBy = currentSort;
          this.handleSortChange();
        }
      };
      
      // Check for sort changes every second
      const sortCheckInterval = setInterval(checkSortChange, 1000);
      state.cleanupHandlers = state.cleanupHandlers || [];
      state.cleanupHandlers.push(() => clearInterval(sortCheckInterval));
    },
    
    // Handle sort order changes
    handleSortChange() {
      if (state.isProcessing) {
        debug.log('Already processing, skipping sort change', null, 'warn');
        return;
      }
      
      state.isProcessing = true;
      const previousSort = state.sortBy;
      state.sortBy = utils.getCurrentSortOrder();
      
      debug.log('Handling sort change', { 
        previousSort, 
        newSort: state.sortBy 
      });
      
      if (this.isCollectionPage()) {
        // For collection pages, save the new product order
        this.saveProductOrder();
      } else if (this.isProductPage()) {
        // For product pages, reload the product order for the new sort
        const savedData = stateManager.loadProductOrder(state.collection, state.sortBy);
        
        if (savedData) {
          state.productHandles = savedData.handles;
          this.setupProductNavigation();
        } else {
          debug.log('No saved order found for sort', { sort: state.sortBy }, 'warn');
          // Try to find the collection and load its products
          this.findAndLoadCollection();
        }
      }
      
      state.isProcessing = false;
    },
    
    // Save product order from collection page
    saveProductOrder() {
      if (!this.isCollectionPage()) return;
      
      const products = utils.getCollectionProducts();
      
      if (products.length > 0) {
        stateManager.saveProductOrder(products, state.sortBy, state.collection);
        
        // Update the current state
        state.productHandles = products.map(p => p.handle);
        
        // If we're on a product page within the collection, update the navigation
        if (this.isProductPage()) {
          this.setupProductNavigation();
        }
      } else {
        debug.log('No products found to save', null, 'warn');
      }
    },
    
    // Set up product navigation
    setupProductNavigation(attempt = 0) {
      if (!this.isProductPage()) {
        debug.log('Not on a product page, skipping navigation setup', null, 'warn');
        return;
      }
      
      if (!state.productHandles || state.productHandles.length === 0) {
        if (attempt < CONFIG.maxRetries) {
          debug.log(`No product handles found, retrying... (${attempt + 1}/${CONFIG.maxRetries})`);
          setTimeout(() => this.setupProductNavigation(attempt + 1), CONFIG.retryDelay);
        } else {
          debug.log('Max retries reached, giving up on navigation setup', null, 'error');
        }
        return;
      }
      
      const currentIndex = state.productHandles.indexOf(state.currentHandle);
      
      if (currentIndex === -1) {
        debug.log('Current product not found in navigation data', { 
          currentHandle: state.currentHandle,
          firstHandle: state.productHandles[0],
          lastHandle: state.productHandles[state.productHandles.length - 1]
        }, 'warn');
        
        // Try to reload the collection data
        if (attempt < 2) {
          debug.log('Attempting to reload collection data...');
          this.findAndLoadCollection();
        }
        return;
      }
      
      debug.log('Setting up product navigation', {
        currentIndex,
        totalProducts: state.productHandles.length,
        prevHandle: state.productHandles[currentIndex - 1],
        nextHandle: state.productHandles[currentIndex + 1]
      });
      
      // Set up the navigation buttons
      this.setupButtons(currentIndex);
    },
    
    // Set up navigation buttons
    setupButtons(currentIndex, attempt = 0) {
      const prevButton = document.querySelector(CONFIG.selectors.prevButton);
      const nextButton = document.querySelector(CONFIG.selectors.nextButton);
      
      if (prevButton && nextButton) {
        debug.log('Setting up navigation buttons', { currentIndex });
        
        // Store current index in state before any async operations
        state.currentIndex = currentIndex;
        
        // Remove existing event listeners to prevent duplicates
        const newPrev = prevButton.cloneNode(true);
        const newNext = nextButton.cloneNode(true);
        
        // Replace buttons in the DOM
        prevButton.parentNode.replaceChild(newPrev, prevButton);
        nextButton.parentNode.replaceChild(newNext, nextButton);
        
        // Update button states
        this.updateButtonStates(newPrev, newNext, currentIndex);
        
        // Handle click events
        const handlePrevClick = (e) => {
          e.preventDefault();
          this.navigateToProduct(state.currentIndex - 1);
        };
        
        const handleNextClick = (e) => {
          e.preventDefault();
          this.navigateToProduct(state.currentIndex + 1);
        };
        
        // Add click handlers
        newPrev.addEventListener('click', handlePrevClick);
        newNext.addEventListener('click', handleNextClick);
        
        // Handle keyboard navigation
        const handleKeyDown = (e) => {
          if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
          
          if (e.key === 'ArrowLeft' && !newPrev.disabled) {
            e.preventDefault();
            this.navigateToProduct(state.currentIndex - 1);
          } else if (e.key === 'ArrowRight' && !newNext.disabled) {
            e.preventDefault();
            this.navigateToProduct(state.currentIndex + 1);
          }
        };
        
        // Add keyboard event listener
        document.addEventListener('keydown', handleKeyDown);
        
        // Store cleanup handlers
        state.cleanupHandlers.push(
          () => document.removeEventListener('keydown', handleKeyDown),
          () => newPrev.removeEventListener('click', handlePrevClick),
          () => newNext.removeEventListener('click', handleNextClick)
        );
        
        debug.log('Navigation buttons initialized', { 
          prevDisabled: newPrev.disabled,
          nextDisabled: newNext.disabled,
          currentIndex: state.currentIndex
        });
      } else if (attempt < CONFIG.maxRetries) {
        debug.log(`Navigation buttons not found, retrying... (${attempt + 1}/${CONFIG.maxRetries})`);
        setTimeout(() => this.setupButtons(currentIndex, attempt + 1), CONFIG.retryDelay);
      } else {
        debug.log('Navigation buttons not found after max retries', null, 'error');
      }
    },
    
    // Update button states
    updateButtonStates(prevButton, nextButton, currentIndex) {
      if (!prevButton || !nextButton) return;
      
      const isFirst = currentIndex <= 0;
      const isLast = currentIndex >= state.productHandles.length - 1;
      
      prevButton.disabled = isFirst;
      nextButton.disabled = isLast;
      
      // Visual feedback
      prevButton.classList.toggle('opacity-50', isFirst);
      prevButton.classList.toggle('cursor-not-allowed', isFirst);
      prevButton.setAttribute('aria-disabled', isFirst);
      
      nextButton.classList.toggle('opacity-50', isLast);
      nextButton.classList.toggle('cursor-not-allowed', isLast);
      nextButton.setAttribute('aria-disabled', isLast);
      
      // Update ARIA labels
      if (isFirst) {
        prevButton.setAttribute('aria-label', 'No previous product');
      } else {
        prevButton.setAttribute('aria-label', 'Previous product');
      }
      
      if (isLast) {
        nextButton.setAttribute('aria-label', 'No next product');
      } else {
        nextButton.setAttribute('aria-label', 'Next product');
      }
      
      debug.log('Updated button states', {
        currentIndex,
        isFirst,
        isLast,
        totalProducts: state.productHandles.length
      });
    },
    
    // Navigate to a specific product
    navigateToProduct(targetIndex) {
      if (targetIndex < 0 || targetIndex >= state.productHandles.length) {
        debug.log('Navigation out of bounds', { targetIndex, maxIndex: state.productHandles.length - 1 });
        return;
      }
      
      const targetHandle = state.productHandles[targetIndex];
      if (!targetHandle) {
        debug.log('No product handle found at index', { targetIndex }, 'error');
        return;
      }
      
      // Update current index in state
      state.currentIndex = targetIndex;
      
      debug.log('Navigating to product', { 
        fromIndex: state.productHandles.indexOf(state.currentHandle),
        toIndex: targetIndex,
        handle: targetHandle,
        currentIndex: state.currentIndex
      });
      
      // Show loading indicator
      this.showLoadingIndicator();
      
      // Update URL and navigate
      const newUrl = `/products/${targetHandle}`;
      window.location.href = newUrl;
      if (state.sortBy && state.sortBy !== 'manual') {
        url.searchParams.set('sort_by', state.sortBy);
      }
      
      // Add a loading indicator
      this.showLoadingIndicator();
      
      // Use smooth navigation if possible
      if (history.pushState) {
        history.pushState({}, '', url.toString());
        window.dispatchEvent(new Event('popstate'));
      }
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Load the new page
      window.location.href = url.toString();
    },
    
    // Show loading indicator
    showLoadingIndicator() {
      let indicator = document.getElementById('product-nav-loading');
      
      if (!indicator) {
        indicator = document.createElement('div');
        indicator.id = 'product-nav-loading';
        indicator.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 3px;
          background: #ff00ff;
          z-index: 9999;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
          pointer-events: none;
        `;
        document.body.appendChild(indicator);
      }
      
      // Trigger animation
      indicator.style.transform = 'scaleX(1)';
      setTimeout(() => {
        indicator.style.transition = 'transform 0.3s ease';
        indicator.style.transform = 'scaleX(0)';
      }, 10);
    },
    
    // Initialize styles
    initStyles() {
      if (document.getElementById('product-navigation-styles')) return;
      
      const style = document.createElement('style');
      style.id = 'product-navigation-styles';
      style.textContent = `
    */
    
        /* Base button styles */
      
        .product-navigation__button {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          border: 1px solid #ddd;
          background: #fff;
          color: #333;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 120px;
          text-align: center;
        }
        
        .product-navigation__button:hover:not(:disabled) {
          background: #f5f5f5;
          border-color: #ccc;
        }
        
        .product-navigation__button:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.3);
        }
        
        .product-navigation__button:disabled,
        .product-navigation__button.opacity-50 {
          opacity: 0.5;
          cursor: not-allowed;
          pointer-events: none;
        }
        
        /* Button container */
        
        .product-navigation {
          display: flex;
          justify-content: space-between;
          margin: 30px 0;
          gap: 15px;
        }
        
        
        /* Button icons */
        
        .product-navigation__icon {
          display: inline-block;
          width: 16px;
          height: 16px;
          margin: 0 5px;
        }
        
        .product-navigation__button--prev .product-navigation__icon {
          margin-right: 8px;
        }
        
        .product-navigation__button--next .product-navigation__icon {
          margin-left: 8px;
        }
        
        
        /* Loading state */
        
        @keyframes productNavPulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        
        .product-navigation__button--loading {
          position: relative;
          color: transparent !important;
        }
        
        .product-navigation__button--loading::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 20px;
          height: 20px;
          margin: -10px 0 0 -10px;
          border: 2px solid #333;
          border-top-color: transparent;
          border-radius: 50%;
          animation: productNavSpin 0.8s linear infinite;
        }
        
        @keyframes productNavSpin {
          to { transform: rotate(360deg); }
        }
        
        
        /* Debug styles */
      
        .product-nav-debug-visible {
          outline: 2px dashed rgba(255, 0, 255, 0.5) !important;
        }
      `;
      
      document.head.appendChild(style);
    },
    
    // Check if current page is a collection page
    isCollectionPage() {
      return document.body.classList.contains('template-collection') || 
             window.location.pathname.includes('/collections/');
    },
    
    // Check if current page is a product page
    isProductPage() {
      return document.body.classList.contains('template-product') || 
             window.location.pathname.includes('/products/');
    },
    
    // Clean up event listeners and observers
    cleanup() {
      debug.log('Cleaning up navigation');
      
      try {
        // Disconnect all observers
        if (state.observers && Array.isArray(state.observers)) {
          state.observers.forEach(observer => {
            try {
              if (observer && typeof observer.disconnect === 'function') {
                debug.log('Disconnecting observer', { type: observer.constructor?.name });
                observer.disconnect();
              }
            } catch (e) {
              debug.log('Error disconnecting observer', { error: e.message }, 'error');
            }
          });
        }
        
        // Remove event listeners
        if (state.cleanupHandlers && Array.isArray(state.cleanupHandlers)) {
          debug.log(`Removing ${state.cleanupHandlers.length} event handlers`);
          state.cleanupHandlers.forEach(handler => {
            try {
              if (typeof handler === 'function') {
                handler();
              }
            } catch (e) {
              debug.log('Error in cleanup handler', { error: e.message }, 'error');
            }
          });
        }
        
        // Reset state
        state.isInitialized = false;
        state.isProcessing = false;
        state.observers = [];
        state.cleanupHandlers = [];
        state.productHandles = [];
        state.currentHandle = null;
        
        debug.log('Cleanup completed');
      } catch (error) {
        debug.log('Error during cleanup', { 
          error: error.message,
          stack: error.stack 
        }, 'error');
      }
    }
  };

  // Main initialization
  const init = () => {
    try {
      // Skip initialization for password pages
      if (utils.isPasswordPage()) {
        debug.log('Password page detected, skipping navigation initialization');
        return;
      }

      // Initialize navigation
      navigation.init();
    } catch (error) {
      debug.log('Fatal error in main init', {
        error: error.message,
        stack: error.stack
      }, 'error');
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  if (CONFIG.debug) {
    window.productNavigation = {
      state,
      navigation,
      utils,
      debug,
      CONFIG
    };
  }

})();